import { v4 as uuidv4 } from "uuid";
import type {
  MealPlanSnapshotV1,
  TrainingPlanSnapshotV1,
} from "@trainrr/api/planRevisions";
import type { Exercise, Workout } from "@/components/workouts/workoutsColumns";
import type { Meal } from "@/components/nutrition/mealColumns";
import type { Food } from "@/components/nutrition/foodColumns";
import { MEAL_IMAGE_BUCKET } from "@/lib/utils/MealUtils";
import { exercisesToExerciseCollection } from "@/lib/utils/WorkoutUtils";

export type TrainingRevisionSnapshot = TrainingPlanSnapshotV1;
export type MealRevisionSnapshot = MealPlanSnapshotV1;

const mealImageUrl = (path: string | null) => {
  if (!path) return null;
  const base = import.meta.env.VITE_SUPABASE_URL.replace(/\/$/, "");
  return {
    path,
    url: `${base}/storage/v1/object/public/${MEAL_IMAGE_BUCKET}/${path}`,
  };
};

const toLegacySets = (
  sets: TrainingRevisionSnapshot["plan"]["workouts"][number]["sections"][number]["exercises"][number]["sets"],
) =>
  sets.map((set) => ({
    id: set.id ?? uuidv4(),
    startRange: set.minReps ?? undefined,
    endRange: set.maxReps ?? undefined,
    targetWeight: set.targetWeight ?? undefined,
    targetTimeSeconds: set.targetSeconds ?? undefined,
    targetDistanceMeters: set.targetDistanceMeters ?? undefined,
  }));

export const workoutsFromTrainingSnapshot = (
  snapshot: TrainingRevisionSnapshot,
): Workout[] =>
  [...snapshot.plan.workouts]
    .sort((left, right) => left.day - right.day)
    .map((workout) => {
      const exercises = workout.sections.flatMap((section) =>
        section.exercises.map(
          (exercise) =>
            ({
              id: exercise.exerciseId,
              workout_exercise_id: exercise.id,
              name: exercise.name,
              ex_order: exercise.exOrder,
              sub_order: exercise.subOrder,
              tempo: exercise.tempo,
              rir: exercise.rir,
              rest_time: exercise.restSeconds,
              coach_note: exercise.coachNote,
              section_id: section.id,
              position: exercise.position,
              superset_key: exercise.supersetKey,
              superset_position: exercise.supersetPosition,
              tracking_type_override: exercise.trackingTypeOverride,
              sets: toLegacySets(exercise.sets),
              sortableId: uuidv4(),
            }) as Exercise,
        ),
      );
      return {
        id: workout.id,
        name: workout.name,
        description: workout.description,
        created_at: "",
        day: workout.day,
        exerciseCollection: exercisesToExerciseCollection(exercises),
      };
    });

export const scheduleFromTrainingSnapshot = (
  snapshot: TrainingRevisionSnapshot,
) => ({
  schedulingMode: snapshot.plan.schedulingMode,
  rollingSchedule: snapshot.plan.rollingSchedule.map((entry) => ({
    id: uuidv4(),
    workout_id: entry.workoutId,
    day_index: entry.dayIndex,
    order_index: entry.orderIndex,
    rolling_period: entry.rollingPeriod,
  })),
  relativeSchedule: snapshot.plan.scheduleEntries
    .filter(
      (entry) =>
        entry.scheduleBasis === "relative" && typeof entry.dayOffset === "number",
    )
    .map((entry) => ({
      id: entry.id ?? uuidv4(),
      workout_id: entry.workoutId,
      day_offset: entry.dayOffset ?? 0,
      order_index: entry.orderIndex,
    }))
    .sort(
      (left, right) =>
        left.day_offset - right.day_offset || left.order_index - right.order_index,
    ),
  calendarSchedule: snapshot.plan.scheduleEntries
    .filter(
      (entry) =>
        entry.scheduleBasis === "absolute" && Boolean(entry.scheduledDate),
    )
    .map((entry) => ({
      id: entry.id ?? uuidv4(),
      workout_id: entry.workoutId,
      scheduled_date: entry.scheduledDate as string,
      order_index: entry.orderIndex,
    }))
    .sort(
      (left, right) =>
        left.scheduled_date.localeCompare(right.scheduled_date) ||
        left.order_index - right.order_index,
    ),
});

export const extraStoredIds = (
  current: { id: string }[],
  next: { id: string }[],
  alreadyDeleted: string[] = [],
) => {
  const keep = new Set(next.map((item) => item.id));
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const id of [...current.map((item) => item.id), ...alreadyDeleted]) {
    if (id.length <= 2 || keep.has(id) || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
};

export const datedCalendarFromTrainingSnapshot = (
  snapshot: TrainingRevisionSnapshot,
  options: { isTemplate: boolean; anchorDate: string },
) => {
  const schedule = scheduleFromTrainingSnapshot(snapshot);
  if (schedule.calendarSchedule.length > 0) {
    return schedule.calendarSchedule;
  }
  if (!options.isTemplate) return [];
  return schedule.relativeSchedule
    .map((entry) => {
      const date = new Date(`${options.anchorDate}T00:00:00`);
      date.setDate(date.getDate() + entry.day_offset);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return {
        id: entry.id,
        workout_id: entry.workout_id,
        scheduled_date: `${year}-${month}-${day}`,
        order_index: entry.order_index,
      };
    })
    .sort(
      (left, right) =>
        left.scheduled_date.localeCompare(right.scheduled_date) ||
        left.order_index - right.order_index,
    );
};

export const mealEditorStateFromSnapshot = (
  snapshot: MealRevisionSnapshot,
  currentMeals: Meal[],
  alreadyDeleted: {
    meals?: string[];
    foods?: { foodId: string; mealId: string }[];
  } = {},
) => {
  const currentFoods = new Map<string, Food>();
  for (const meal of currentMeals) {
    for (const food of meal.foods) {
      currentFoods.set(food.id, food);
    }
  }

  const meals: Meal[] = snapshot.plan.meals.map((meal) => ({
    id: meal.id,
    name: meal.name,
    notes: meal.notes,
    meal_number: meal.mealNumber,
    image_path: meal.imagePath,
    image: mealImageUrl(meal.imagePath),
    foods: meal.foods.map((food) => {
      const existing = currentFoods.get(food.foodId);
      return {
        id: food.foodId,
        name: food.name,
        serving_type: food.servingType ?? existing?.serving_type ?? "",
        default_serving_size:
          food.defaultServingSize ?? existing?.default_serving_size ?? 1,
        serving_size: food.servingSize,
        calories: food.calories,
        protein: food.protein,
        carb: food.carb,
        fat: food.fat,
      };
    }),
  }));

  const nextMealIds = new Set(meals.map((meal) => meal.id));
  const deletedMeals = extraStoredIds(
    currentMeals,
    meals,
    alreadyDeleted.meals,
  );
  const deletedFoods: { foodId: string; mealId: string }[] = [];
  const seenFoods = new Set<string>();
  const pushDeletedFood = (foodId: string, mealId: string) => {
    const key = `${mealId}:${foodId}`;
    if (seenFoods.has(key)) return;
    seenFoods.add(key);
    deletedFoods.push({ foodId, mealId });
  };
  for (const meal of currentMeals) {
    if (!nextMealIds.has(meal.id)) continue;
    const nextFoodIds = new Set(
      meals.find((item) => item.id === meal.id)?.foods.map((food) => food.id) ??
        [],
    );
    for (const food of meal.foods) {
      if (!nextFoodIds.has(food.id)) {
        pushDeletedFood(food.id, meal.id);
      }
    }
  }
  for (const deleted of alreadyDeleted.foods ?? []) {
    if (!nextMealIds.has(deleted.mealId)) continue;
    const nextFoodIds = new Set(
      meals.find((item) => item.id === deleted.mealId)?.foods.map(
        (food) => food.id,
      ) ?? [],
    );
    if (!nextFoodIds.has(deleted.foodId)) {
      pushDeletedFood(deleted.foodId, deleted.mealId);
    }
  }

  return {
    name: snapshot.plan.name,
    meals,
    deletedMeals,
    deletedFoods,
  };
};
